import { Button, ButtonRow, Heading, Link, Text } from '@ksyos/design-system';
import { Link as ReactRouterLink } from '@ksyos/router';
import type { DatadogRumUser } from '@frontend/utils/datadogRumUtils';
import { serviceDeskEmailAddress } from '@common/constants';
import { AuthorizationErrorCode } from '@common/enums';
import { BackgroundImageTemplate } from '@frontend/components/templates/BackgroundImageTemplate';
import { getSelectedLanguage } from '@frontend/translate';

type GeneralErrorProps = {
    errorMessage?: string;
    rumSessionId?: string;
    rumUser?: DatadogRumUser;
    statusCode?: number;
};

export function GeneralError({ errorMessage, statusCode, rumUser, rumSessionId }: GeneralErrorProps) {
    const errorPageTranslation = getSelectedLanguage().errorPages;

    return (
        <BackgroundImageTemplate>
            <Heading
                mb={6}
                size="xlarge"
            >
                {errorPageTranslation.generalTitle}
            </Heading>
            <Text mb={6}>{generalBodyText()}</Text>
            {rumUser && (
                <>
                    <Text
                        mb={6}
                        size="sm"
                    >
                        {`${errorPageTranslation.userId}: ${rumUser.id}`}
                        <br />
                        {`${errorPageTranslation.organizationId}: ${rumUser.organizationId}`}
                        <br />
                        {rumSessionId && `${errorPageTranslation.rumSessionId}: ${rumSessionId}`}
                    </Text>
                </>
            )}
            <ButtonRow
                isStackedBelowSmall={true}
                sx={{ marginBottom: 4 }}
            >
                <Button
                    as={ReactRouterLink}
                    color="secondary"
                    iconLeft="home"
                    to="/home"
                >
                    {errorPageTranslation.goToHome}
                </Button>
                <Button
                    as={ReactRouterLink}
                    color="secondary"
                    iconLeft="search"
                    to="/patients"
                >
                    {errorPageTranslation.goToPatients}
                </Button>
            </ButtonRow>

            <Text
                mb={1}
                size="xs"
            >
                {`${errorPageTranslation.generalNeedHelp} `}
                <Link
                    color="secondary"
                    href="tel:+31206000060"
                    variant="externalLink"
                >
                    020 600 00 60
                </Link>
            </Text>
            <Text
                mb={1}
                size="xs"
            >
                {errorPageTranslation.generalOpeningHours}
            </Text>
            <Text size="xs">
                <Link
                    color="secondary"
                    href={`mailto:${serviceDeskEmailAddress}`}
                    variant="externalLink"
                >
                    {errorPageTranslation.generalSendEmail}
                </Link>
            </Text>
        </BackgroundImageTemplate>
    );

    function generalBodyText(): string {
        const isAuthorizationError =
            statusCode === 403 ||
            (errorMessage && (Object.values(AuthorizationErrorCode) as string[]).includes(errorMessage));

        return isAuthorizationError ? errorPageTranslation.missingAuthorizationBody : errorPageTranslation.generalBody;
    }
}
